import { CircularProgress } from '@mui/material';
import classNames from 'classnames';
import React, { FC, ReactNode, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { removeModal } from 'src/features/globalSlice';
import { Modal, ModalType } from 'src/store/models/Modal';
import { useLoadingSelector } from 'src/utils/useLoadingSelector';
import useMountTransition from 'src/utils/useMountTransition';

import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import AutoAssignModal from '../AutoAssignModal/AutoAssignModal';
import BasicModal from '../BasicModal/BasicModal';
import ChangePasswordModal from '../ChangePasswordModal/ChangePasswordModal';
import ErrorsModal from '../ErrorsModal/ErrorsModal';
import ParametersModal from '../ParametersModal/ParametersModal';
import PinModal from '../PinModal/PinModal';
import ProductPreviewModal from '../ProductPreviewModal/ProductPreviewModal';
import RemoveCompanyModal from '../RemoveCompanyModal/RemoveCompanyModal';
import ShadowBox from '../ShadowBox/ShadowBox';
import SuccessModal from '../SuccessModal/SuccessModal';
import WarningModal from '../WarningModal/WarningModal';
import classes from './ModalProvider.module.scss';

interface ModalProviderProps {
  children: ReactNode;
  className?: string;
  transitionDuration?: number;
}

const ModalProvider: FC<ModalProviderProps> = ({
  children,
  transitionDuration = 300,
  className,
}) => {
  const dispatch = useAppDispatch();
  const globalModal = useAppSelector((state) => state.global.modal);
  const [modal, setModal] = useState<Modal>(null);
  const open = !!globalModal;

  const shouldRender = useMountTransition(open, transitionDuration);

  const loading = useLoadingSelector({
    actionType: globalModal?.loadingAction,
  });

  useEffect(() => {
    if (globalModal) {
      setModal(globalModal);
    }
  }, [globalModal]);

  const showModalType = () => {
    switch (modal?.modalType) {
      case ModalType.PIN:
        return (
          <PinModal modal={modal} transitionDuration={transitionDuration} />
        );
      case ModalType.WARNING:
        return <WarningModal modal={modal} />;
      case ModalType.DETECTED_ERRORS:
        return <ErrorsModal modal={modal} />;
      case ModalType.SUCCESS:
        return <SuccessModal modal={modal} />;
      case ModalType.EDIT_PARAMETERS:
        return <ParametersModal modal={modal} />;
      case ModalType.BASIC:
        return <BasicModal modal={modal} />;
      case ModalType.CHANGE_PASSWORD:
        return <ChangePasswordModal modal={modal} />;
      case ModalType.PRODUCT_PREVIEW:
        return <ProductPreviewModal modal={modal} />;
      case ModalType.REMOVE_COMPANY:
        return <RemoveCompanyModal modal={modal} />;
      case ModalType.AUTO_ASSIGN:
        return <AutoAssignModal modal={modal} />;
      default:
        return null;
    }
  };

  return (
    <>
      {children}
      {(shouldRender || open) &&
        createPortal(
          <div
            style={{ transitionDuration: `${transitionDuration}ms` }}
            className={classNames({
              [classes.Backdrop]: true,
              [classes.BackdropAnimateIn]: open && shouldRender,
              className,
            })}
            onClick={
              loading
                ? undefined
                : globalModal?.onBackdropClose ?? true
                ? () => dispatch(removeModal())
                : undefined
            }
          >
            <ShadowBox
              onClick={(event) => event.stopPropagation()}
              className={classes.ModalContent}
            >
              {!loading ? (
                showModalType()
              ) : (
                <div className={classes.Loading}>
                  <CircularProgress color="primary" size="3rem" />
                </div>
              )}
            </ShadowBox>
          </div>,
          document.body
        )}
    </>
  );
};

export default ModalProvider;
